/**
 * Currency Store
 * Manages currency preferences and conversions
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/services/api'

export type SupportedCurrency = 'USD' | 'ZMW'

export interface CurrencyInfo {
  code: string
  name: string
  symbol: string
  icon: string // Icon name from lucide-react
}

interface CurrencyState {
  // Current selected currency
  currency: SupportedCurrency
  
  // Exchange rates
  rates: Record<string, number>
  lastUpdated: string | null
  
  // Actions
  setCurrency: (currency: SupportedCurrency) => void
  loadRates: () => Promise<void>
  convert: (amount: number, from: SupportedCurrency, to?: SupportedCurrency) => number
  formatCurrency: (amount: number, currency?: SupportedCurrency) => string
  getCurrencyInfo: (currency: SupportedCurrency) => CurrencyInfo
}

const currencyInfo: Record<SupportedCurrency, CurrencyInfo> = {
  USD: {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    icon: 'DollarSign', // Lucide icon name
  },
  ZMW: {
    code: 'ZMW',
    name: 'Zambian Kwacha',
    symbol: 'K',
    icon: 'Coins', // Lucide icon name
  },
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      currency: 'USD',
      rates: {
        'USD_ZMW': 27.5, // Default fallback
        'ZMW_USD': 1 / 27.5,
      },
      lastUpdated: null,

      setCurrency: (currency: SupportedCurrency) => {
        console.log('[Currency] Setting currency to:', currency)
        set({ currency })
        
        // Update user settings on backend (non-blocking)
        apiClient.put('/user/settings', { currency }).catch(error => {
          console.error('[Currency] Failed to update currency preference:', error)
        })
        
        // Force reload rates when currency changes
        get().loadRates()
      },

      loadRates: async () => {
        try {
          console.log('[Currency] Loading exchange rates...')
          const response = await apiClient.get('/currency/rates')
          
          if (response.data.success && response.data.data.rates) {
            const rates: Record<string, number> = {}
            
            response.data.data.rates.forEach((rate: any) => {
              if (rate.currency === 'ZMW') {
                rates['USD_ZMW'] = rate.rate
                console.log('[Currency] USD to ZMW rate:', rate.rate)
              } else if (rate.currency === 'USD') {
                rates['ZMW_USD'] = rate.rate
                console.log('[Currency] ZMW to USD rate:', rate.rate)
              }
            })
            
            set({
              rates,
              lastUpdated: response.data.data.timestamp,
            })
            
            console.log('[Currency] Exchange rates loaded successfully:', rates)
          }
        } catch (error) {
          console.error('[Currency] Failed to load exchange rates:', error)
          // Keep using cached/fallback rates
        }
      },

      convert: (amount: number, from: SupportedCurrency, to?: SupportedCurrency) => {
        const targetCurrency = to || get().currency
        
        if (from === targetCurrency) return amount
        
        const { rates } = get()
        const rateKey = `${from}_${targetCurrency}`
        const rate = rates[rateKey] || 1
        
        console.log(`[Currency] Converting ${amount} ${from} to ${targetCurrency} using rate ${rate}`)
        
        return amount * rate
      },

      formatCurrency: (amount: number, currency?: SupportedCurrency) => {
        const curr = currency || get().currency
        const info = currencyInfo[curr]
        
        const decimals = 2
        const formatted = amount.toFixed(decimals)
        
        // Format with thousands separator
        const parts = formatted.split('.')
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        
        return `${info.symbol}${parts.join('.')}`
      },

      getCurrencyInfo: (currency: SupportedCurrency) => {
        return currencyInfo[currency]
      },
    }),
    {
      name: 'currency-storage',
      partialize: (state) => ({
        currency: state.currency,
        rates: state.rates,
        lastUpdated: state.lastUpdated,
      }),
    }
  )
)

// Load rates on app start - but only in browser and only once
if (typeof window !== 'undefined') {
  // Use a flag in sessionStorage to ensure we only load once per session
  const hasLoadedRates = sessionStorage.getItem('currency-rates-loaded')
  
  if (!hasLoadedRates) {
    // Mark as loaded immediately to prevent duplicate calls
    sessionStorage.setItem('currency-rates-loaded', 'true')
    
    // Delay initial load to avoid blocking and prevent initialization loops
    setTimeout(() => {
      useCurrencyStore.getState().loadRates().catch(err => {
        console.error('Initial rate load failed:', err)
        // Clear the flag so it can retry on next session
        sessionStorage.removeItem('currency-rates-loaded')
      })
    }, 3000) // 3 second delay
  }
  
  // Refresh rates every hour
  const ratesInterval = setInterval(() => {
    useCurrencyStore.getState().loadRates().catch(err => {
      console.error('Rate refresh failed:', err)
    })
  }, 3600000) // 1 hour
  
  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    clearInterval(ratesInterval)
  })
}
